/* eslint-disable max-len */
/* eslint-disable object-curly-newline */
import React, { useContext, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Modal, Box } from '@mui/material';
// eslint-disable-next-line import/no-extraneous-dependencies
import { X, Eye, EyeOff, Check } from 'lucide-react';
import API from '../../api/endpoints';
import { sendRequest } from '../../api/shootAPI';
import { NotificationContext } from '../../contexts/Notification';

const labelClass = 'block text-sm font-medium text-ink-900 mb-1.5';
const inputClass = 'w-full box-border h-10 pl-3 pr-10 border border-rule rounded-[8px] text-sm text-ink-900 placeholder:text-ink-500 focus:outline-none focus:border-brand-500';

function PasswordField({ label, name, value, onChange }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <span className={labelClass}>{label}</span>
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          name={name}
          value={value}
          onChange={onChange}
          placeholder="Required"
          className={inputClass}
          style={{ borderStyle: 'solid' }}
        />
        <button
          type="button"
          aria-label={show ? 'Hide password' : 'Show password'}
          onClick={() => setShow((s) => !s)}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-500 hover:text-ink-900 bg-transparent border-0 cursor-pointer p-1 flex items-center"
        >
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </div>
  );
}

PasswordField.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

function Requirement({ met, text }) {
  return (
    <li className="flex items-center gap-2 text-xs">
      <span
        className="inline-flex items-center justify-center rounded-full flex-shrink-0"
        style={{ width: 14, height: 14, background: met ? '#E7F6E6' : '#eef0f4' }}
      >
        {met && <Check size={10} className="text-[#256D23]" strokeWidth={3} />}
      </span>
      <span className={met ? 'text-[#256D23]' : 'text-ink-500'}>{text}</span>
    </li>
  );
}

Requirement.propTypes = {
  met: PropTypes.bool.isRequired,
  text: PropTypes.string.isRequired,
};

function ChangePasswordModal({ email, closeModal }) {
  const { displayNotification } = useContext(NotificationContext);
  const [password, setPassword] = useState({ current: '', new: '', confirm: '' });
  const [saving, setSaving] = useState(false);

  const passwordRequirements = useMemo(() => ({
    characterMin: !!(password.new && /^(?=.{8,})/.test(password.new)),
    oneLowerAndUpper: /^(?=.*[a-z])(?=.*[A-Z])/.test(password.new),
    oneNumber: /^(?=.*[0-9])/.test(password.new),
    confirm: !!(password.new && password.confirm && password.new === password.confirm),
  }), [password.new, password.confirm]);

  const handlePassword = (e) => {
    setPassword({ ...password, [e.target.name]: e.target.value });
  };

  const disableSave = !(
    password.current
    && Object.values(passwordRequirements).every((requirement) => !!requirement)
  );

  const handleSubmit = async () => {
    setSaving(true);
    const response = await sendRequest({
      method: 'put',
      url: API.USER.changePassword,
      data: {
        email,
        currentPassword: password.current,
        newPassword: password.new,
      },
    });
    if (response.success) {
      displayNotification({
        message: 'Password changed successfully!',
        severity: 'success',
      });
      closeModal();
    } else {
      displayNotification({
        message: response.data.error,
        severity: 'error',
      });
    }
    setSaving(false);
  };

  return (
    <Modal
      open
      onClose={closeModal}
      className="flex-row align-c jstfy-c"
      data-testid="change-password-modal"
    >
      <Box style={{ width: 460, maxWidth: 'calc(100% - 32px)', maxHeight: '90vh', overflowY: 'auto', background: '#fff', borderRadius: 12, fontFamily: 'Inter, sans-serif' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-3">
          <h5 className="text-lg font-semibold leading-none text-ink-900" data-testid="modal-title">Update Password</h5>
          <button type="button" aria-label="Close" onClick={closeModal} className="text-ink-500 hover:text-ink-900 bg-transparent border-0 cursor-pointer p-0">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 pt-2 pb-5 flex flex-col gap-4">
          <PasswordField label="Current Password" name="current" value={password.current} onChange={handlePassword} />
          <PasswordField label="New Password" name="new" value={password.new} onChange={handlePassword} />
          <PasswordField label="Confirm New Password" name="confirm" value={password.confirm} onChange={handlePassword} />

          <ul className="flex flex-col gap-1.5 m-0 p-0 list-none">
            <Requirement met={passwordRequirements.characterMin} text="At least 8 characters" />
            <Requirement met={passwordRequirements.oneLowerAndUpper} text="Upper & lowercase letters" />
            <Requirement met={passwordRequirements.oneNumber} text="At least one number" />
            <Requirement met={passwordRequirements.confirm} text="New passwords match" />
          </ul>
        </div>

        {/* Footer */}
        <div className="flex justify-end items-center gap-2 px-6 pb-5 pt-1">
          <button type="button" onClick={closeModal} className="inline-flex items-center justify-center box-border h-10 px-4 border border-rule rounded-[8px] text-sm font-medium text-ink-900 bg-white hover:bg-gray-50 transition" style={{ borderStyle: 'solid' }}>Cancel</button>
          <button
            type="button"
            disabled={disableSave || saving}
            onClick={handleSubmit}
            className="inline-flex items-center justify-center box-border h-10 px-4 bg-[#53A551] text-white rounded-[8px] text-sm font-semibold hover:bg-[#458a44] disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </Box>
    </Modal>
  );
}

ChangePasswordModal.propTypes = {
  email: PropTypes.string.isRequired,
  closeModal: PropTypes.func.isRequired,
};

export default ChangePasswordModal;
